/**
 * Checkbox Component
 *
 * Premium-styled checkbox for tables and forms.
 *
 * @package MultilingualPressZone
 */

import { el } from '../utils/dom.js';

/**
 * @typedef {Object} CheckboxProps
 * @property {boolean} [checked]
 * @property {boolean} [disabled]
 * @property {boolean} [indeterminate]
 * @property {string} [label] Accessible label (aria-label when label is hidden)
 * @property {Function|null} [onChange] (checked, event) => void
 * @property {string} [className]
 * @property {boolean} [compact]
 * @property {Object} [attrs] Extra attributes applied to the input element
 */

/**
 * Render an accessible checkbox.
 *
 * The component returns a <label> wrapping a visually-hidden checkbox input
 * and a custom control element. For table use-cases, omit visible label text
 * and rely on `aria-label`.
 *
 * @param {CheckboxProps} props
 */
export function Checkbox({
    checked = false,
    disabled = false,
    indeterminate = false,
    label = '',
    onChange = null,
    className = '',
    compact = false,
    attrs = {}
} = {}) {
    const classes = [
        'presszone-multilingual-checkbox',
        compact ? 'presszone-multilingual-checkbox--compact' : '',
        className
    ].filter(Boolean);

    const input = el('input', {
        type: 'checkbox',
        class: 'presszone-multilingual-checkbox__input',
        checked: checked ? true : undefined,
        disabled: disabled || undefined,
        'aria-label': label || undefined,
        onchange: (e) => {
            if (disabled) return;
            if (typeof onChange === 'function') onChange(!!e.target.checked, e);
        },
        ...attrs
    });

    // Indeterminate is a property, not an attribute.
    input.indeterminate = !!indeterminate;

    return el('label', { class: classes.join(' ') },
        input,
        el('span', { class: 'presszone-multilingual-checkbox__control', 'aria-hidden': 'true' }),
        // Intentionally omit a visible label for compact/table use.
        // Use aria-label on the input for accessibility.
        null
    );
}

export default Checkbox;
